import { DialogComponent } from '@theme/dialog';
import { renderTextWithLineBreaks } from '@theme/utilities';

/**
 * <store-conditions-drawer> — singleton right slide-in drawer showing a
 * partner store's special conditions.
 *
 * Shell markup: snippets/store-conditions-drawer.liquid (same drawer chrome
 * as the subscription drawer). Opening is event-driven, mirroring the
 * subscription/cart drawers: any surface dispatches
 * `theme:store-conditions:open` on `document` with
 * `detail: { conditions, storeName }` — the store cards
 * (assets/ng-store-card.js) do this from their Special Conditions trigger.
 *
 * The native <dialog> (via DialogComponent.showDialog) provides the modal
 * focus trap, Esc/outside-click dismissal, and restores focus to the trigger
 * on close.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} storeName
 * @property {HTMLElement} conditionsText
 * @property {HTMLButtonElement} closeButton
 *
 * @extends {DialogComponent}
 */
class StoreConditionsDrawer extends DialogComponent {
  /** Typed view of the base component's refs (same casting approach as subscription-drawer.js). */
  get #els() {
    return /** @type {any} */ (this.refs);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('theme:store-conditions:open', this.#onOpenEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('theme:store-conditions:open', this.#onOpenEvent);
  }

  /** @param {Event} event */
  #onOpenEvent = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail?.conditions) return;

    const { storeName, conditionsText } = this.#els;
    if (storeName) storeName.textContent = detail.storeName || '';
    if (conditionsText) renderTextWithLineBreaks(conditionsText, detail.conditions);

    this.showDialog();
  };

  /** Close handler for the declarative `on:click="store-conditions-drawer/close"` binding. */
  close() {
    this.closeDialog();
  }
}

if (!customElements.get('store-conditions-drawer')) {
  customElements.define('store-conditions-drawer', StoreConditionsDrawer);
}
